import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { serverContext, appContext } from './App';
import { checkUserAuthorizationForRestricted, toGeoJson } from './utils';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import LinearProgress from '@material-ui/core/LinearProgress';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import SelectUsergroups from './Components/SelectUsergroups'
import TagsArray from './Components/TagsArray.js';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { securityType, annotationType } from './commonTypes';
import { useDropzone } from 'react-dropzone';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useTags } from './customEffects';
import { TabPanel } from './Components/TabPanel';
import AnnotationMap from './Map/AnnotationMap.js';

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  formControl: {
    minWidth: 180,
  },
  formControlTags: {
    marginLeft: -8,
  },
  formControlRestricted: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  progress: {
    marginTop: 4,
    width: '100%',
  },
}));


const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" {...props} ref={ref} />;
});


const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out'
};

const activeStyle = {
  borderColor: '#2196f3'
};

const acceptStyle = {
  borderColor: '#00e676'
};

const rejectStyle = {
  borderColor: '#ff1744'
};


const DropzoneAnnotation = props => {

  const [files2Upload, setFiles2Upload] = useState([]);

  const onDrop = useCallback(acceptedFiles => {

    setFiles2Upload(acceptedFiles);
    if (props.onClearError) {
      props.onClearError();
    }

    if (acceptedFiles && props.onFileDrop)
      props.onFileDrop(acceptedFiles);

  }, [props]);

  const onDropRejected = useCallback(() => {
    setFiles2Upload([]);
    if (props.onInvalidFile) {
      props.onInvalidFile('Invalid file format. Please upload a GeoJSON/JSON file.');
    }
  }, [props]);

  useEffect(() => {
    if (props.clearSelectionSignal !== undefined) {
      setFiles2Upload([]);
    }
  }, [props.clearSelectionSignal]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: false,
    accept: [
      'application/json',
      'application/geo+json',
      'application/vnd.geo+json'
    ]
  });

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {}),
  }), [isDragActive, isDragReject, isDragAccept]);

  return (
    <div className="container">
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <p>Drag 'n' drop a file, or click to select it</p>
      </div>
      <aside>
        {files2Upload.map(file => (
          <li key={file.name}>{file.name}</li>
        ))}
      </aside>
      {props.errorMessage && (
        <p style={{ color: '#d32f2f', marginTop: 8 }}>{props.errorMessage}</p>
      )}
    </div>
  )
}



const validationSchema = Yup.object({
  title: Yup.string()
    .matches(/^[a-zA-Z0-9 _-]+$/, 'Title can only contain letters, numbers, spaces, underscores, and hyphens')
    .max(16, 'Title must be 16 characters or less')
    .required('Title is required'),
  description: Yup.string()
    .max(127, 'Description must be 127 characters or less'),
  annotationType: Yup.string().required('Annotation Type is required')
    .oneOf(annotationType.map(doc => doc.value), 'Invalid annotation type'),
  restricted: Yup.boolean(),
  securityClassification: Yup.string().oneOf(securityType.map(sec => sec.value), 'Invalid security classification'),
  usergroups: Yup.array(),
  tags: Yup.array(),
  features: Yup.array()
    .min(1, 'Features array must not be empty')
    .required('Features are required'),
  // __force_dirty_flag: Yup.number(), 
});


const createInitialValues = {
  title: '',
  description: '',
  annotationType: 'GeoJson',
  securityClassification: 'UNCLASSIFIED',
  restricted: false,
  usergroups: [],
  tags: [],
  features: [],
 // __force_dirty_flag: Date.now(), // force change
};

const getAnnotationValues = (annotation) => ({
  title: annotation?.title || '',
  description: annotation?.description || '',
  annotationType: annotation?.annotationType || 'GeoJson',
  securityClassification: annotation?.securityClassification || 'UNCLASSIFIED',
  restricted: annotation?.restricted || false,
  usergroups: annotation?.usergroups || [],
  tags: annotation?.tags || [],
  features: annotation?.features || null,
});

const normalizeGeoJsonInput = (input) => {
  const candidate = input?.geoJson ?? input;

  if (!candidate) {
    return null;
  }

  if (candidate.type === 'FeatureCollection') {
    return {
      type: 'FeatureCollection',
      features: Array.isArray(candidate.features) ? candidate.features : [],
    };
  }

  if (candidate.type === 'Feature') {
    return {
      type: 'FeatureCollection',
      features: [candidate],
    };
  }

  if (candidate.type && candidate.coordinates) {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: candidate,
          properties: {},
        },
      ],
    };
  }

  return null;
};

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const isValidGeometry = (geometry) => {
  if (geometry === null) return true;
  if (!isObject(geometry) || typeof geometry.type !== 'string') return false;

  const geometryTypes = new Set([
    'Point',
    'MultiPoint',
    'LineString',
    'MultiLineString',
    'Polygon',
    'MultiPolygon',
    'GeometryCollection',
  ]);

  if (!geometryTypes.has(geometry.type)) return false;

  if (geometry.type === 'GeometryCollection') {
    return Array.isArray(geometry.geometries) && geometry.geometries.every(isValidGeometry);
  }

  return geometry.coordinates !== undefined;
};

const isValidFeature = (feature) => {
  if (!isObject(feature) || feature.type !== 'Feature') return false;
  if (!('geometry' in feature)) return false;
  if (!isValidGeometry(feature.geometry)) return false;
  if ('properties' in feature && feature.properties !== null && !isObject(feature.properties)) return false;
  return true;
};

const isValidGeoJson = (data) => {
  if (!isObject(data)) return false;

  if (data.type === 'FeatureCollection') {
    return Array.isArray(data.features) && data.features.every(isValidFeature);
  }

  if (data.type === 'Feature') {
    return isValidFeature(data);
  }

  return isValidGeometry(data);
};


/**
 * CreateAnnotationDlg renders a dialog that accepts the file and its metadata .
 * The Ok button is disabled until the form is valid.
 */
function CreateAnnotationDlg(props) {
  const { server } = useContext(serverContext);
  const { dispatchApp } = useContext(appContext);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const { tags } = useTags(server.serverHost, server.token);
  const [restrictedEditDisabled, setRestrictedEditDisabled] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [annotation, setAnnotation] = useState(null);
  const [geoJsonData, setGeoJsonData] = useState(toGeoJson(annotation));
  const [dropzoneClearSignal, setDropzoneClearSignal] = useState(0);
  const [importErrorMessage, setImportErrorMessage] = useState('');

  useEffect(() => {
    const getAnnotation = async () => {
      if (props.mode === 'edit') {
        try {
          const response = await axios({
            method: 'get',
            url: `${server.serverHost}/api/annotations?id=${props.annotationId}`,
            headers: {
              'Authorization': `Basic ${server.token}`,
            }
          });
          setAnnotation(response.data);

        } catch (error) {
          console.error('Error fetching annotation:', error);
        }
      }
    };
    getAnnotation();
  }, [props.annotationId]);

  useEffect(() => {
    if (props.mode === 'edit' && annotation) {
      formik.setValues(getAnnotationValues(annotation));
    } else if (props.mode === 'create') {
      formik.setValues(createInitialValues);
    }
  }, [props.mode]);



  useEffect(() => {
    setGeoJsonData(toGeoJson(annotation));
  }, [annotation]);



  const theme = useTheme();
  const classes = useStyles();

  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

const initialValues = useMemo(() => {
  if (props.mode === 'create') {
    return createInitialValues;
  }
  // for edit mode, only re-compute when the annotation._id changes:
  return annotation
    ? getAnnotationValues(annotation)
    : createInitialValues;
}, [props.mode, annotation?._id]);

const formik = useFormik({
  initialValues,
  validationSchema,
  enableReinitialize: true,    // now only re-inits when annotation._id changes
});

  useEffect(() => {
    const updateValues = async () => {
      if (formik.dirty) {
        try {
          await validationSchema.validate(formik.values, { abortEarly: false });

        } catch (validationErrors) {
          const errors = {};
          validationErrors.inner.forEach((error) => {
            errors[error.path] = error.message;
          });
          formik.setErrors(errors);
        }
      }
    };
    updateValues();
  }, [formik.values, formik.dirty]);

  useEffect(() => {
    if (props.mode === 'edit') {
      if (server && annotation) {
        const ugroups = server.usergroups.filter(g => annotation?.usergroups.includes(g._id));
        setSelectedUserGroups(ugroups || []);
        setSelectedTags(annotation?.tags || []);
        
   //     formik.setFieldValue('features', annotation?.features, true);
   //     formik.setFieldTouched('features', true, false);

        if(annotation.restricted) {
          const enabled = checkUserAuthorizationForRestricted(server, annotation);
          setRestrictedEditDisabled(!enabled);
        } else {
          setRestrictedEditDisabled(false);
        }     
      }
    } else {
      setRestrictedEditDisabled(false);
     // setRestrictedEditDisabled(server.userRole !== 'superAdmin' && server.userRole !== 'admin' && server.userRole !== 'groupAdmin');
    }
  }, [props, props.mode, annotation, server, server.userRole]);



  // Trigger form submission when Ok is pressed.
  const handleCreateAnnotation = async () => {
    try {
      setShowProgress(true);

      // Construct the payload
      const payload = {
        title: formik.values.title,
        description: formik.values.description,
        annotationType: formik.values.annotationType,
        securityClassification: formik.values.securityClassification,
        restricted: formik.values.restricted,
        usergroups: selectedUserGroups.map(g => g._id),
        tags: selectedTags,
        type: 'FeatureCollection',
        features: formik.values.features
      };

      const headers = {
        'Authorization': `Basic ${server.token}`,
        'Content-Type': 'application/json'
      };

      if (props.mode === 'create') {
        await axios.post(
          `${server.serverHost}/api/annotations`,
          payload,
          {
            headers,
            onUploadProgress: progressEvent => {
              const pct = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setProgress(pct);
            }
          }
        );
      } else {
        await axios.put(
          `${server.serverHost}/api/annotations/${annotation._id}`,
          payload,
          { headers }
        );
      }

      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Annotation  ${formik.values.title} ${props.mode === 'create'? 'created' : 'edited'}.` } });

    } catch (error) {
      let errorMessage = `Error ${props.mode === 'create' ? 'creating' : 'updating'} annotation: `;

      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with a non-2xx status
          const { status, statusText, data } = error.response;
          if (typeof data === 'string') {
            errorMessage += data;
          } else if (data?.message) {
            errorMessage += data.message;
          } else if (data?.error) {
            errorMessage += data.error;
          } else {
            errorMessage += `HTTP ${status}: ${statusText}`;
          }
        } else if (error.request) {
          // Request made but no response
          errorMessage += 'No response received from server. Please check your network connection.';
        } else {
          // Something wrong setting up the request
          errorMessage += error.message;
        }
      } else {
        // Non-Axios error
        errorMessage += (error).message || 'An unknown error occurred.';
      }

      console.error('Annotation creation/updating failed:', error);
      dispatchApp({
        type: 'SHOW_SNACKBAR_MESSAGE',
        payload: { message: errorMessage, severity: 'error' }
      });

    } finally {
      setShowProgress(false);
      // Close the dialog or reset form on success
      props.onClose();
    }
  };
  


  const handleUserGroupsChange = ugs => {
    setSelectedUserGroups(ugs);
    formik.setFieldValue("usergroups", ugs.map(g => g._id), true); // `true` triggers validation and dirty tracking
  }

  const handleTagsChange = selectedTags => {
    setSelectedTags(selectedTags);
    formik.setFieldValue("tags", selectedTags, true);
  }


  const handleAnnotationFileDrop = files => {

    if (!files || files.length === 0) return;

    const file = files[0];

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const parsedContent = JSON.parse(reader.result);
        if (Array.isArray(parsedContent) || !isValidGeoJson(parsedContent)) {
          setImportErrorMessage('Invalid GeoJSON file. The uploaded JSON must be a GeoJSON object (FeatureCollection, Feature, or Geometry), not an array.');
          setDropzoneClearSignal(prev => prev + 1);
          return;
        }

        setImportErrorMessage('');
        handleAnnotationUpdate(parsedContent);
        setTabValue(1);

      } catch (err) {
        setImportErrorMessage('Invalid JSON file. Please upload a valid GeoJSON file.');
        setDropzoneClearSignal(prev => prev + 1);
      }
    };

    reader.onerror = () => {
      console.error("Error reading file:", reader.error);
      setImportErrorMessage('Could not read the uploaded file. Please try again.');
      setDropzoneClearSignal(prev => prev + 1);
    };

    reader.readAsText(file);
  }

  const handleInvalidImportFile = (message) => {
    setImportErrorMessage(message);
    setDropzoneClearSignal(prev => prev + 1);
  };

  const handleClearImportError = () => {
    setImportErrorMessage('');
  };

  

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAnnotationUpdate = (data) => {
    const normalizedGeoJson = normalizeGeoJsonInput(data);
    if (!normalizedGeoJson) {
      return;
    }

    const features = Array.isArray(normalizedGeoJson.features) ? normalizedGeoJson.features : [];

    // update Formik's value & mark as touched so validation runs
    formik.setFieldValue('features', features, true);
   // formik.setFieldTouched('features', true, true);
  //  formik.setFieldValue('__force_dirty_flag', Date.now(), false);
    formik.setFieldTouched('features', true, false);

    // still keep your local copy if you need it elsewhere
    setAnnotation(prev => ({
      ...prev,
      features,
   //   __force_dirty_flag: Date.now(), // force change
    }));
    setGeoJsonData(normalizedGeoJson);
  };

  useEffect(() => {
    console.log('Dirty:', formik.dirty);
    console.log('isValid:', formik.isValid);
  }, [formik.dirty, formik.isValid]);

  return (
    <Dialog
      open={true}
      TransitionComponent={Transition}
      onClose={props.onClose}
      fullWidth={true}
      fullScreen={fullScreen}
    >
      <DialogTitle>{`${props.mode === 'create' ? ' 📄 Create' : '📝 Edit'} Annotation`}</DialogTitle>
      <DialogContent style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div className={classes.root} style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
          <Tabs indicatorColor="secondary" textColor="secondary"
            value={tabValue}
            onChange={handleTabChange}
          >
            <Tab label="General" />
            <Tab label="Map" />
            {props.mode === 'create' && <Tab label="Import" />}         
          </Tabs>
          <TabPanel value={tabValue} index={0} >
            <div style={{ flexGrow: 1, marginTop: 16 }}>
              {/** Tab 1: General */}
              <div>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", flexGrow: 1, marginTop: 16 }}>
                  <div style={{ display: "flex", width: "100%", gap: "16px" }} >
                    <FormControl className={classes.formControl} style={{ flex: 7 }} >
                      <TextField
                        label="Title"
                        variant="standard"
                        disabled={restrictedEditDisabled}
                        fullWidth
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="title"
                        error={formik.touched.title && Boolean(formik.errors.title)}
                        helperText={formik.touched.title && formik.errors.title}
                      />
                    </FormControl>
                    <FormControl style={{ flex: 3 }}>
                      <InputLabel>Annotation Type</InputLabel>
                      <Select
                        fullWidth
                        disabled={restrictedEditDisabled}
                        value={formik.values.annotationType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="annotationType"
                      >
                        {annotationType.map(t => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <FormControl className={classes.formControl} style={{ width: "100%" }}>
                    <TextField
                      label="Description"
                      variant="standard"
                      fullWidth
                      disabled={restrictedEditDisabled}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="description"
                      error={formik.touched.description && Boolean(formik.errors.description)}
                      helperText={formik.touched.description ? formik.errors.description : ''}
                    />
                  </FormControl>
                  <div style={{ display: "flex", width: "100%", gap: "16px" }}>
                    <FormControl className={classes.formControl} style={{ flex: 7 }} 
                      disabled={restrictedEditDisabled}
                    >
                      <InputLabel>User Groups</InputLabel>
                      <SelectUsergroups
                        canDeleteDemo
                        selectedUserGroups={selectedUserGroups}
                        onUserGroupsChange={handleUserGroupsChange}
                      />
                    </FormControl>
                    <FormControl className={classes.formControl} style={{ flex: 3 }} 
                      disabled={restrictedEditDisabled}
                    >
                      <InputLabel>Security Classification</InputLabel>
                      <Select
                        fullWidth
                        value={formik.values.securityClassification}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="securityClassification"
                      >
                        {securityType.map(sec => (
                          <MenuItem key={sec.value} value={sec.value}>{sec.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div style={{ display: "flex", width: "100%", gap: "16px" }}>
                    <FormControl className={classes.formControlTags} style={{ flex: 7 }}>    
                      <TagsArray tags={tags.map(tag => tag.title)} selectedTags={selectedTags} disabled={restrictedEditDisabled} onChange={(event, newValue) => handleTagsChange(newValue)} />
                    </FormControl>
                    <FormControl className={classes.formControlRestricted} style={{ flex: 3 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formik.values.restricted}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            name="restricted"
                            color="secondary"
                          />
                        }
                        disabled={restrictedEditDisabled}
                        label="Restricted"
                        labelPlacement="start"
                      />
                    </FormControl>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <AnnotationMap
              width={'100%'}
              height={400}
              lastMapCenter={props.lastMapCenter}
              disabled={restrictedEditDisabled}
              geoJsonData={geoJsonData}
              onUpdated={handleAnnotationUpdate}
            />
          </TabPanel>
          {/** Tab 3: Import */}
          {props.mode === 'create' && <TabPanel value={tabValue} index={2} >
            <DropzoneAnnotation
              onFileDrop={handleAnnotationFileDrop}
              onInvalidFile={handleInvalidImportFile}
              onClearError={handleClearImportError}
              clearSelectionSignal={dropzoneClearSignal}
              errorMessage={importErrorMessage}
            />
          </TabPanel>
          }

          {showProgress && <LinearProgress variant="determinate" value={progress} />}
          <DialogActions style={{ justifyContent: "flex-end", marginTop: "auto" }}>
            <Button onClick={props.onClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleCreateAnnotation} color="secondary" disabled={!formik.isValid || !formik.dirty}>
              Ok
            </Button>
          </DialogActions>
        </div>
      </DialogContent>
    </Dialog>
  );
}


CreateAnnotationDlg.propTypes = {
  annotationId: PropTypes.string,
  lastMapCenter: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
};


export default CreateAnnotationDlg;
